import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProFeature } from '../components/ProFeatures';
import { 
  Users, 
  Car, 
  Wrench, 
  Package, 
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Plus,
  ArrowRight,
  Clock,
  Crown,
  Lock,
  Truck
} from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardPage() {
  const { user, isPro } = useAuth();
  const { checkProAccess, UpgradeModalComponent } = useProFeature();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    reception: 'Recepción',
    in_progress: 'En Progreso',
    completed: 'Completado',
    delivered: 'Entregado'
  };

  const quickActions = [
    { label: 'Nuevo Cliente', icon: Users, to: '/clients', color: 'bg-blue-500' },
    { label: 'Nuevo Servicio', icon: Wrench, to: '/services/new', color: 'bg-amber-500' },
    { label: 'Proveedores', icon: Truck, to: '/suppliers', color: 'bg-indigo-500' },
    { label: 'Inventario', icon: Package, to: '/inventory', color: 'bg-purple-500' },
    { label: 'Finanzas', icon: DollarSign, to: '/finance', color: 'bg-green-500' },
  ];

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <UpgradeModalComponent />
      
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          ¡Hola, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-slate-500">
          Aquí está el resumen de tu taller
        </p>
      </div>

      {/* Stats Grid */}
      <div className="bento-grid mb-8">
        {/* Clients */}
        <div className="stat-card" data-testid="stat-clients">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            {!isPro && (
              <span className="text-xs text-slate-500">
                {stats?.total_clients || 0}/5 gratis
              </span>
            )}
          </div>
          {loading ? (
            <Skeleton className="h-8 w-16 mb-1" />
          ) : (
            <p className="stat-card-value">{stats?.total_clients || 0}</p>
          )}
          <p className="stat-card-label">Clientes</p>
        </div>

        {/* Prospects */}
        <div className="stat-card" data-testid="stat-prospects">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-3">
            <Users className="w-5 h-5 text-slate-600" />
          </div>
          {loading ? (
            <Skeleton className="h-8 w-16 mb-1" />
          ) : (
            <p className="stat-card-value">{stats?.total_prospects || 0}</p>
          )}
          <p className="stat-card-label">Prospectos</p>
        </div>

        {/* Vehicles */}
        <div className="stat-card" data-testid="stat-vehicles">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-3">
            <Car className="w-5 h-5 text-emerald-600" />
          </div>
          {loading ? (
            <Skeleton className="h-8 w-16 mb-1" />
          ) : (
            <p className="stat-card-value">{stats?.total_vehicles || 0}</p>
          )}
          <p className="stat-card-label">Vehículos</p>
        </div>

        {/* Active Services */}
        <div className="stat-card" data-testid="stat-active-services">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-3">
            <Wrench className="w-5 h-5 text-amber-600" />
          </div>
          {loading ? (
            <Skeleton className="h-8 w-16 mb-1" />
          ) : (
            <p className="stat-card-value">{stats?.active_services || 0}</p>
          )}
          <p className="stat-card-label">Servicios Activos</p>
        </div>

        {/* Completed Services */}
        <div className="stat-card bento-span-2" data-testid="stat-completed">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              {loading ? (
                <Skeleton className="h-8 w-20 mb-1" />
              ) : (
                <p className="stat-card-value">{stats?.completed_services || 0}</p>
              )}
              <p className="stat-card-label">Servicios Completados</p>
            </div>
          </div>
        </div>

        {/* Finance Summary */}
        <div className="stat-card bento-span-2 relative" data-testid="stat-finance">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Resumen Financiero</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-2xl font-bold text-emerald-600">
                ${(stats?.total_income || 0).toLocaleString('es-MX')}
              </p>
              <p className="text-sm text-slate-500">Ingresos</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                ${(stats?.total_expenses || 0).toLocaleString('es-MX')}
              </p>
              <p className="text-sm text-slate-500">Gastos</p>
            </div>
          </div>
        </div>

        {/* Low Stock Alert */}
        {stats?.low_stock_items > 0 && (
          <div className="stat-card border-amber-200 bg-amber-50" data-testid="low-stock-alert">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-200 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <p className="font-semibold text-amber-800">{stats.low_stock_items} artículos</p>
                <p className="text-sm text-amber-600">con stock bajo</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Acciones Rápidas</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {quickActions.map((action) => {
            const isLocked = action.pro && !isPro;
            
            return (
              <Link
                key={action.label}
                to={isLocked ? '#' : action.to}
                onClick={(e) => {
                  if (isLocked) {
                    e.preventDefault();
                    checkProAccess(() => {});
                  }
                }}
                className="quick-action group"
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                {isLocked && (
                  <div className="absolute top-2 right-2">
                    <Lock className="w-4 h-4 text-slate-400" />
                  </div>
                )}
                <div className={`quick-action-icon ${action.color}`}>
                  <action.icon className="w-5 h-5" />
                </div>
                <span className="font-medium text-slate-700 group-hover:text-slate-900">
                  {action.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Services */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">Servicios Recientes</h2>
          <Link to="/services" className="text-[#1E3A5F] text-sm font-medium hover:underline flex items-center gap-1">
            Ver todos <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        ) : stats?.recent_services?.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_services.map((service) => (
              <Link
                key={service.service_id}
                to={`/services/${service.service_id}`}
                className="card-base card-hover p-4 flex items-center gap-4 block"
                data-testid={`recent-service-${service.service_id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{service.description}</p>
                  <p className="text-sm text-slate-500 truncate">
                    {service.vehicle} • {service.client}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium status-${service.status}`}>
                    {statusLabels[service.status] || service.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Wrench className="empty-state-icon" />
            <p className="text-slate-500">No hay servicios recientes</p>
            <Link to="/services/new" className="btn-primary inline-flex items-center gap-2 mt-4">
              <Plus className="w-4 h-4" /> Crear servicio
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
